import { TranslateService } from '@ngx-translate/core';
import { environment } from '@root/src/environments/environment';
import { LocalizationMap } from '@models/system-health.models';

export function buildLanguageCandidates(translateService: TranslateService): string[] {
  const availableLangs: { value: string; iso: string }[] = (environment.langs ?? []).map(lang => ({
    value: lang.value.toLowerCase(),
    iso: (lang as any).iso?.toLowerCase() ?? lang.value.split('-')[0].toLowerCase()
  }));

  const translateLangs: string[] = (translateService.getLangs?.() ?? []).map(lang => lang.toLowerCase());
  const currentLang: string = (translateService.currentLang ||
    translateService.defaultLang ||
    environment.defaultLang ||
    availableLangs[0]?.value ||
    translateLangs[0] ||
    'en').toLowerCase();
  const defaultLang: string = (translateService.defaultLang || environment.defaultLang || '').toLowerCase();
  const candidates = new Set<string>();

  const pushLang = (lang: string | undefined) => {
    if (!lang) return;
    const normalized = lang.toLowerCase();
    candidates.add(normalized);
    const base = normalized.split('-')[0];
    candidates.add(base);

    const regionVariant = availableLangs.find(item => item.value.startsWith(base + '-'))?.value;
    if (regionVariant) candidates.add(regionVariant);
  };

  pushLang(currentLang);
  pushLang(defaultLang);
  translateLangs.forEach(pushLang);
  availableLangs.forEach(lang => {
    pushLang(lang.value);
    pushLang(lang.iso);
  });

  return Array.from(candidates);
}

export function findLocalizationValue(localization: LocalizationMap, key: string): string | undefined {
  if (localization[key]) {
    return localization[key];
  }

  const normalizedKey = key.toLowerCase();
  for (const locKey of Object.keys(localization)) {
    if (locKey.toLowerCase() === normalizedKey && localization[locKey]) {
      return localization[locKey];
    }
  }

  return undefined;
}

export function resolveLocalizedName(
  localization: LocalizationMap | null | undefined,
  fallback: string,
  translateService: TranslateService
): string {
  if (!localization) {
    return fallback;
  }

  const candidates = buildLanguageCandidates(translateService);
  for (const candidate of candidates) {
    const value = findLocalizationValue(localization, candidate);
    if (value) {
      return value;
    }
  }

  const firstNonEmpty = Object.values(localization).find(val => !!val && val.trim().length > 0);
  return firstNonEmpty || fallback;
}

export function humanizeName(value: string): string {
  if (!value) return value;
  const shortName = value.split('.').pop() ?? value;
  return shortName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
}

export function toLocalizationMap(value: any): LocalizationMap | null {
  if (!value) return null;
  if (typeof value === 'object') {
    return value;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
}
